import React, { useEffect, useState, useRef } from 'react';
import { FactCheck } from '../data/mockData';
interface TimelineProps {
  currentTime: number;
  duration: number;
  factChecks: FactCheck[];
  onSeek: (time: number) => void;
  isPlaying: boolean;
}
export const Timeline: React.FC<TimelineProps> = ({
  currentTime,
  duration,
  factChecks,
  onSeek,
  isPlaying
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [hoveredFact, setHoveredFact] = useState<FactCheck | null>(null);
  const [touchedFact, setTouchedFact] = useState<FactCheck | null>(null);
  // Format seconds to MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  // Calculate position percentage
  const getPositionPercent = (time: number): number => {
    return time / duration * 100;
  };
  // Handle timeline click
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      const clickPosition = e.clientX - rect.left;
      const clickPercent = clickPosition / rect.width;
      const newTime = clickPercent * duration;
      onSeek(newTime);
    }
  };
  // Handle fact marker click
  const handleFactClick = (e: React.MouseEvent<HTMLDivElement>, fact: FactCheck) => {
    e.stopPropagation(); // Prevent the timeline click from triggering
    onSeek(fact.timestamp);
    setHoveredFact(null);
  };
  // Handle fact marker touch
  const handleFactTouch = (e: React.TouchEvent<HTMLDivElement>, fact: FactCheck) => {
    e.stopPropagation(); // Prevent the timeline touch from triggering
    setTouchedFact(fact);
    // Hide the tooltip after 3 seconds
    setTimeout(() => {
      setTouchedFact(null);
    }, 3000);
  };
  // Group nearby fact checks to prevent overlap on mobile
  const groupedFactChecks = factChecks.reduce((groups, fact) => {
    // Find if this fact is close to an existing group
    const closeGroup = groups.find(group => {
      return group.some(groupFact => {
        // Consider facts within 15 seconds as "close" - increased from 10 to reduce marker density
        return Math.abs(groupFact.timestamp - fact.timestamp) < 15;
      });
    });
    if (closeGroup) {
      closeGroup.push(fact);
    } else {
      groups.push([fact]);
    }
    return groups;
  }, [] as FactCheck[][]);
  return <div className="mb-4" data-id="element-129">
      <h3 className="text-lg font-medium mb-2" data-id="element-130">Timeline</h3>
      <div ref={timelineRef} className="relative h-12 md:h-8 bg-gray-200 rounded-md cursor-pointer" onClick={handleTimelineClick} data-id="element-131">
        {/* Progress bar */}
        <div className="absolute h-full bg-blue-100 rounded-l-md" style={{
        width: `${getPositionPercent(currentTime)}%`
      }} data-id="element-132" />
        {/* Current position indicator */}
        <div className={`absolute w-2 h-full bg-blue-500 transition-all ${isPlaying ? 'duration-100' : ''}`} style={{
        left: `calc(${getPositionPercent(currentTime)}% - 4px)`
      }} data-id="element-133" />
        {/* Fact check markers - grouped for mobile */}
        {groupedFactChecks.map((group, groupIndex) => {
        // Use the average timestamp for the group
        const avgTimestamp = group.reduce((sum, fact) => sum + fact.timestamp, 0) / group.length;
        const avgPosition = getPositionPercent(avgTimestamp);
        return <div key={groupIndex} className="absolute" style={{
          left: `${avgPosition}%`,
          top: '0',
          height: '100%',
          transform: 'translateX(-50%)',
          zIndex: 10
        }} data-id="element-134">
              <div className={`w-3 h-3 md:w-2.5 md:h-2.5 bg-red-500 rounded-full top-1/2 -translate-y-1/2 transform relative flex items-center justify-center shadow-sm transition-transform ${hoveredFact || touchedFact ? 'scale-110' : ''}`} onMouseEnter={() => setHoveredFact(group[0])} onMouseLeave={() => setHoveredFact(null)} onClick={e => handleFactClick(e, group[0])} onTouchStart={e => handleFactTouch(e, group[0])} aria-label={`Fact check at ${formatTime(avgTimestamp)}`} data-id="element-135">
                {group.length > 1 && <span className="absolute -top-3 -right-2 w-3.5 h-3.5 text-white text-[8px] font-bold bg-red-600 rounded-full flex items-center justify-center" data-id="element-136">
                    {group.length}
                  </span>}
                {/* Tooltip for hover on desktop */}
                {hoveredFact && hoveredFact === group[0] && <div className="absolute bottom-full mb-2 bg-white p-2 rounded shadow-md text-xs w-48 z-20 left-1/2 -translate-x-1/2" data-id="element-137">
                    <div className="font-medium" data-id="element-138">{hoveredFact.speaker}</div>
                    <div className="truncate" data-id="element-139">{hoveredFact.claim}</div>
                    <div className="mt-1 text-xs" data-id="element-140">
                      <span className={`px-1 py-0.5 rounded ${hoveredFact.rating === 'True' ? 'bg-green-100' : hoveredFact.rating === 'False' ? 'bg-red-100' : 'bg-yellow-100'}`} data-id="element-141">
                        {hoveredFact.rating}
                      </span>
                    </div>
                  </div>}
                {/* Tooltip for touch on mobile */}
                {touchedFact && touchedFact === group[0] && <div className="absolute bottom-full mb-2 bg-white p-2 rounded shadow-md text-xs w-48 z-20 left-1/2 -translate-x-1/2" data-id="element-142">
                    <div className="font-medium" data-id="element-143">{touchedFact.speaker}</div>
                    <div className="truncate" data-id="element-144">{touchedFact.claim}</div>
                    <div className="mt-1 text-xs" data-id="element-145">
                      <span className={`px-1 py-0.5 rounded ${touchedFact.rating === 'True' ? 'bg-green-100' : touchedFact.rating === 'False' ? 'bg-red-100' : 'bg-yellow-100'}`} data-id="element-146">
                        {touchedFact.rating}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-blue-600" data-id="element-147">
                      Tap to seek to this point
                    </div>
                  </div>}
              </div>
            </div>;
      })}
        {/* Time indicators */}
        <div className="absolute top-full mt-1 w-full flex justify-between text-xs text-gray-600" data-id="element-148">
          <span data-id="element-149">0:00</span>
          <span data-id="element-150">{formatTime(duration / 4)}</span>
          <span data-id="element-151">{formatTime(duration / 2)}</span>
          <span data-id="element-152">{formatTime(duration * 3 / 4)}</span>
          <span data-id="element-153">{formatTime(duration)}</span>
        </div>
      </div>
      <div className="mt-4 text-sm text-gray-600" data-id="element-154">
        <span data-id="element-155">Current Time: {formatTime(currentTime)}</span>
        <span className="mx-2" data-id="element-156">|</span>
        <span data-id="element-157">Total Duration: {formatTime(duration)}</span>
        <span className="mx-2" data-id="element-158">|</span>
        <span className="flex items-center" data-id="element-159">
          <span className="inline-block w-2.5 h-2.5 bg-red-500 rounded-full mr-1" data-id="element-160"></span>
          Fact Check Points
        </span>
      </div>
    </div>;
};